import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Brand } from "@/components/common/Brand";
import {
  LayoutDashboard, Wallet, Users, Boxes, Activity, Plug, FileBarChart,
  Settings as SettingsIcon, ChevronDown, TrendingUp, Receipt, PiggyBank,
  Coins, LineChart, Percent, Clock, ShieldCheck, FileCheck2, FileText,
  FileSignature, ScrollText, ClipboardCheck, FolderLock, ListChecks, Bell, HelpCircle, Banknote, Landmark, Tags,
} from "lucide-react";

const financeChildren = [
  { to: "/finance", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/finance/revenue", label: "Revenue", icon: TrendingUp },
  { to: "/finance/expenses", label: "Expenses", icon: Receipt },
  { to: "/finance/budget", label: "Budget", icon: PiggyBank },
  { to: "/finance/cash-flow", label: "Cash Flow", icon: Coins },
  { to: "/finance/customer-profitability", label: "Customer Profitability", icon: Users },
  { to: "/finance/product-profitability", label: "Product Profitability", icon: Boxes },
  { to: "/finance/labour", label: "Labour", icon: Clock },
  { to: "/finance/gst", label: "GST", icon: Percent },
  { to: "/finance/payments", label: "Payments", icon: Banknote },
];

const topLevel = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/invoices", label: "Invoices", icon: Receipt },
];

const legalChildren = [
  { to: "/legal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/legal/templates", label: "Templates", icon: FileText },
  { to: "/legal/contracts", label: "Contracts", icon: FileSignature },
  { to: "/legal/customers", label: "Customers", icon: Users },
  { to: "/legal/generator", label: "Document Generator", icon: ScrollText },
  { to: "/legal/review", label: "Legal Review", icon: ClipboardCheck },
  { to: "/legal/executed", label: "Executed Documents", icon: FolderLock },
  { to: "/legal/register", label: "Legal Register", icon: ListChecks },
  { to: "/legal/alerts", label: "Alerts & Renewals", icon: Bell },
  { to: "/legal/settings", label: "Settings", icon: SettingsIcon },
];

const bottomLevel = [
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/products", label: "Products", icon: Boxes },
  { to: "/commercial/packages", label: "Commercial Packages", icon: Tags },
  { to: "/products/molaroo/finance", label: "Molaroo Finance", icon: Activity },
  { to: "/usage", label: "Usage", icon: Activity },
  { to: "/integrations", label: "Integrations", icon: Plug },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
  { to: "/settings/banking", label: "Banking & Payments", icon: Landmark },
  { to: "/help", label: "Help", icon: HelpCircle },
];

const linkCls = ({ isActive }) =>
  `group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
    isActive
      ? "bg-cyan-500/10 text-cyan-300 shadow-[inset_2px_0_0_0_#22D3EE]"
      : "text-slate-400 hover:bg-[#17233B] hover:text-slate-100"
  }`;

const subLinkCls = ({ isActive }) =>
  `flex items-center gap-2.5 rounded-md py-1.5 pl-3 pr-2 text-[13px] transition-colors ${
    isActive ? "text-cyan-300 font-semibold" : "text-slate-400 hover:text-slate-100"
  }`;

export const Sidebar = ({ onNavigate }) => {
  const location = useLocation();
  const [financeOpen, setFinanceOpen] = useState(location.pathname.startsWith("/finance"));
  const [legalOpen, setLegalOpen] = useState(location.pathname.startsWith("/legal"));

  return (
    <aside className="flex h-full w-64 flex-col border-r border-[#1E2D4A] bg-[#080D1A]">
      <div className="flex h-16 items-center border-b border-[#1E2D4A] px-5">
        <Brand />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {topLevel.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} onClick={onNavigate} data-testid={`nav-${l.label.toLowerCase()}`}>
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}

        {/* Finance group */}
        <div>
          <button
            onClick={() => setFinanceOpen((o) => !o)}
            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
              location.pathname.startsWith("/finance") ? "text-cyan-300" : "text-slate-400 hover:bg-[#17233B] hover:text-slate-100"
            }`}
            data-testid="nav-finance-toggle"
          >
            <Wallet className="h-4 w-4" /> Finance
            <ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform ${financeOpen ? "rotate-180" : ""}`} />
          </button>
          {financeOpen && (
            <div className="ml-4 mt-1 space-y-0.5 border-l border-[#1E2D4A] pl-2">
              {financeChildren.map((c) => (
                <NavLink key={c.to} to={c.to} end={c.end} className={subLinkCls} onClick={onNavigate}
                  data-testid={`nav-finance-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                  {c.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        <div className="my-3 border-t border-[#1E2D4A]" />

        {/* Legal & Contracts group */}
        <div>
          <button
            onClick={() => setLegalOpen((o) => !o)}
            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
              location.pathname.startsWith("/legal") ? "text-cyan-300" : "text-slate-400 hover:bg-[#17233B] hover:text-slate-100"
            }`}
            data-testid="nav-legal-toggle"
          >
            <FileCheck2 className="h-4 w-4" /> Legal & Contracts
            <ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform ${legalOpen ? "rotate-180" : ""}`} />
          </button>
          {legalOpen && (
            <div className="ml-4 mt-1 space-y-0.5 border-l border-[#1E2D4A] pl-2">
              {legalChildren.map((c) => (
                <NavLink key={c.to} to={c.to} end={c.end} className={subLinkCls} onClick={onNavigate}
                  data-testid={`nav-legal-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                  {c.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        <div className="my-3 border-t border-[#1E2D4A]" />

        {bottomLevel.map((l) => (
          <NavLink key={l.to} to={l.to} className={linkCls} onClick={onNavigate} data-testid={`nav-${l.label.toLowerCase()}`}>
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-[#1E2D4A] px-4 py-3">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Secure internal system · v1.0
        </div>
      </div>
    </aside>
  );
};
